/* <u1-video-carousel>: carrusel de videos de Videowise con el diseño de UNIT 1 (section u1-videowise-reviews-wrapper,
   modo "Carrusel UNIT 1"). De Videowise se usan solo los videos: la lista pública del widget (bulk.json) se pide recién
   cuando la section se acerca a la pantalla. Cada card muestra el clip liviano, mudo y en loop, solo mientras se ve;
   al tocarla se abre el video completo con sonido. Si la lista no llega o viene vacía, la section se oculta entera.
   Carrusel como el de Flash Deals: con mouse, flechas de a una card y snap; en pantallas táctiles, scroll nativo libre.
   Los textos (aria) llegan de los locales por data-l-*. */
const reduce = matchMedia('(prefers-reduced-motion: reduce)');
const mouse = matchMedia('(hover: hover) and (pointer: fine)');
const API = 'https://api-cdn.videowise.com/widget/bulk.json';

class U1VideoCarousel extends HTMLElement {
  connectedCallback() {
    if (this._on) return;
    this._on = true;
    this.row = this.querySelector('[data-vc-row]');
    if (!this.row || !this.dataset.widget || !this.dataset.product) return this.hideSection();
    const io = new IntersectionObserver(entries => {
      if (!entries.some(e => e.isIntersecting)) return;
      io.disconnect();
      this.load();
    }, { rootMargin: '800px 0px' });
    io.observe(this);
  }

  hideSection() {
    (this.closest('.u1-videowise') || this).hidden = true;
  }

  async load() {
    const d = this.dataset;
    const q = new URLSearchParams({
      widget: d.widget,
      shop: d.shop,
      productId: d.product,
      device: mouse.matches ? 'DESKTOP' : 'MOBILE',
      locale: d.locale || 'en',
    });
    let videos = [];
    try {
      const res = await fetch(`${API}?${q}`);
      const json = await res.json();
      const w = json && json.widgets && json.widgets[0];
      videos = ((w && w.videos) || []).filter(v => v && (v.url || clipOf(v)));
    } catch (err) {
      videos = [];
    }
    videos = videos.slice(0, parseInt(d.max, 10) || 12);
    if (!videos.length) return this.hideSection();
    this.render(videos);
  }

  render(videos) {
    const frag = document.createDocumentFragment();
    videos.forEach((v, i) => {
      const profile = v.socialProfile || {};
      const handle = profile.userName ? `@${profile.userName}` : '';
      const poster = (v.poster && v.poster.url) || (v.thumbnail && v.thumbnail.url) || '';
      const card = document.createElement('button');
      card.type = 'button';
      card.className = 'vc-card';
      card.style.setProperty('--i', i);
      card.setAttribute('aria-label', handle ? `${this.dataset.lPlay} ${handle}` : this.dataset.lPlay);

      const video = document.createElement('video');
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      video.preload = 'none';
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('aria-hidden', 'true');
      if (poster) video.poster = poster;
      video.dataset.src = v.url || clipOf(v);
      card.append(video);

      if (handle) {
        const who = document.createElement('span');
        who.className = 'vc-who u1-text-body-s';
        if (profile.profilePhotoUrl) {
          const img = document.createElement('img');
          img.src = profile.profilePhotoUrl;
          img.alt = '';
          img.width = 28;
          img.height = 28;
          img.loading = 'lazy';
          who.append(img);
        }
        const name = document.createElement('span');
        name.textContent = handle;
        who.append(name);
        card.append(who);
      }

      const secs = Math.round(Number(v.duration) || 0);
      if (secs > 0) {
        const dur = document.createElement('span');
        dur.className = 'vc-dur u1-text-body-xs--bold';
        dur.setAttribute('aria-hidden', 'true');
        dur.textContent = `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`;
        card.append(dur);
      }

      const play = document.createElement('span');
      play.className = 'vc-play';
      play.setAttribute('aria-hidden', 'true');
      card.append(play);

      card.addEventListener('click', () => this.open(v.url || video.dataset.src, poster));
      frag.append(card);
    });
    this.row.replaceChildren(frag);
    this.classList.add('is-ready');
    this.watchVideos();
    this.initNav();
  }

  /* Un solo clip a la vez: arranca el primero que se ve, dura data-cycle segundos y pasa al siguiente visible.
     Con mouse, el que está bajo el cursor se reproduce y la rotación sigue desde ahí al salir. Se reproduce mudo y
     el src se pone recién la primera vez que le toca */
  watchVideos() {
    this._seen = new Set();
    if (reduce.matches) return;
    this._cycle = Math.max(2, parseFloat(this.dataset.cycle) || 6) * 1000;
    this._io = new IntersectionObserver(entries => {
      entries.forEach(e => {
        const video = e.target.querySelector('video');
        if (!video) return;
        if (e.isIntersecting) this._seen.add(video); else this._seen.delete(video);
      });
      if (this._modal || this._hover) return;
      if (!this._active || !this._seen.has(this._active)) this.advance(true);
      else if (!this._timer) this.schedule();
    }, { threshold: 0.6 });
    this.row.querySelectorAll('.vc-card').forEach(card => {
      this._io.observe(card);
      card.addEventListener('mouseenter', () => {
        if (!mouse.matches || this._modal) return;
        this._hover = true;
        clearTimeout(this._timer);
        this._timer = null;
        this.playOnly(card.querySelector('video'));
      });
      card.addEventListener('mouseleave', () => {
        if (!mouse.matches) return;
        this._hover = false;
        if (!this._modal) this.schedule();
      });
    });
  }

  visibleVideos() {
    return [...this.row.querySelectorAll('.vc-card video')].filter(v => this._seen.has(v));
  }

  /* El siguiente visible después del actual (o el primero), en el orden del carrusel */
  advance(first) {
    const list = this.visibleVideos();
    if (!list.length) { this.playOnly(null); return; }
    const i = list.indexOf(this._active);
    const next = first && i < 0 ? list[0] : list[(i + 1) % list.length];
    this.playOnly(next);
    this.schedule();
  }

  schedule() {
    clearTimeout(this._timer);
    this._timer = setTimeout(() => { this._timer = null; this.advance(false); }, this._cycle);
  }

  playOnly(video) {
    this.row.querySelectorAll('.vc-card video').forEach(v => { if (v !== video) v.pause(); });
    this.row.querySelectorAll('.vc-card.is-playing').forEach(c => c.classList.remove('is-playing'));
    this._active = video;
    if (!video) return;
    if (!video.src && video.dataset.src) video.src = video.dataset.src;
    video.closest('.vc-card').classList.add('is-playing');
    const p = video.play();
    if (p && p.catch) p.catch(() => {});
  }

  initNav() {
    const prev = this.querySelector('[data-nav="prev"]');
    const next = this.querySelector('[data-nav="next"]');
    if (!prev || !next) return;
    const row = this.row;
    const sync = () => {
      if (!mouse.matches) return;
      const max = row.scrollWidth - row.clientWidth;
      prev.disabled = row.scrollLeft <= 2;
      next.disabled = row.scrollLeft >= max - 2;
    };
    [prev, next].forEach(b => b.addEventListener('click', () => {
      const card = row.querySelector('.vc-card');
      const step = card ? card.offsetWidth + (parseFloat(getComputedStyle(row).columnGap) || 0) : 300;
      row.scrollBy({ left: b === next ? step : -step, behavior: reduce.matches ? 'auto' : 'smooth' });
    }));
    row.addEventListener('scroll', sync, { passive: true });
    if ('ResizeObserver' in window) new ResizeObserver(sync).observe(row);
    mouse.addEventListener('change', sync);
    sync();
  }

  /* Video completo con sonido en un dialog (Escape y el fondo lo cierran; al cerrar se corta la descarga) */
  open(src, poster) {
    let dlg = this.querySelector('dialog.vc-modal');
    if (!dlg) {
      dlg = document.createElement('dialog');
      dlg.className = 'vc-modal';
      const vid = document.createElement('video');
      vid.controls = true;
      vid.playsInline = true;
      vid.setAttribute('playsinline', '');
      const close = document.createElement('button');
      close.type = 'button';
      close.className = 'vc-close';
      close.setAttribute('aria-label', this.dataset.lClose);
      close.addEventListener('click', () => dlg.close());
      dlg.append(vid, close);
      dlg.addEventListener('click', e => { if (e.target === dlg) dlg.close(); });
      dlg.addEventListener('close', () => {
        vid.pause();
        vid.removeAttribute('src');
        vid.load();
        this._modal = false;
        /* un instante despues: si se reanuda en el mismo evento del cierre, el navegador lo ignora */
        if (!reduce.matches) setTimeout(() => this.advance(true), 120);
      });
      this.append(dlg);
    }
    const vid = dlg.querySelector('video');
    vid.poster = poster || '';
    vid.src = src;
    this._modal = true;
    clearTimeout(this._timer);
    this._timer = null;
    this.row.querySelectorAll('.vc-card video').forEach(v => v.pause());
    dlg.showModal();
    const p = vid.play();
    if (p && p.catch) p.catch(() => {});
  }
}

function clipOf(v) {
  return v && v.meta && v.meta.convertedH264 && v.meta.convertedH264.clipUrl;
}

if (!customElements.get('u1-video-carousel')) customElements.define('u1-video-carousel', U1VideoCarousel);
